import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";

const Terms = () => {
  return (
    <div className="container max-w-4xl py-12">
      <div className="space-y-4">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold">Terms of Use</h1>
          <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
        </div>
        
        <Separator className="my-6" />
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">1. Introduction</h2>
          <p>
            Welcome to WillTheyConvert. These Terms of Use ("Terms") govern your use of our website, services, and applications (collectively, the "Services") operated by WillTheyConvert ("we," "us," or "our"), a company registered in Poland.
          </p>
          <p>
            By accessing or using our Services, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access or use our Services.
          </p>
          <p>
            These Terms are governed by Polish law and EU regulations, including but not limited to the GDPR and consumer protection laws.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">2. Legal Framework</h2>
          <p>
            Our Services operate under the following legal framework:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Polish Civil Code (Kodeks cywilny)</li>
            <li>Polish Consumer Rights Act (Ustawa o prawach konsumenta)</li>
            <li>EU General Data Protection Regulation (GDPR)</li>
            <li>Polish Personal Data Protection Act (Ustawa o ochronie danych osobowych)</li>
            <li>Electronic Services Provision Act (Ustawa o świadczeniu usług drogą elektroniczną)</li>
          </ul>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">3. Service Description</h2>
          <p>
            WillTheyConvert provides a platform for creating and deploying simulated product validation tests ("Tests"). Our service is currently offered as a lifetime deal, giving you perpetual access to the features included in your purchase.
          </p>
          <p>
            The simulated checkout process is designed to gauge market interest and collect user feedback without processing actual payments. All test participants are clearly informed that they are participating in a product validation test.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">4. Consumer Rights</h2>
          <p>
            If you are a consumer within the EU, you have the following rights under EU consumer protection laws:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Right to withdraw from the contract within 14 days without giving any reason (for digital content, this right may be lost upon immediate access with your explicit consent)</li>
            <li>Right to receive clear and comprehensible information about our Services</li>
            <li>Right to receive services conforming to the contract</li>
            <li>Right to data portability</li>
            <li>Right to complain to relevant supervisory authorities</li>
          </ul>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">5. Account Registration</h2>
          <p>
            To access our Services, you need to register for an account. When you register, you agree to:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Provide accurate, current, and complete information;</li>
            <li>Maintain and promptly update your account information;</li>
            <li>Keep your account credentials secure and confidential;</li>
            <li>Promptly notify us of any unauthorized use of your account or other security breaches;</li>
            <li>Accept responsibility for all activities that occur under your account.</li>
          </ul>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">6. Lifetime Access</h2>
          <p>
            Our current offering is a lifetime deal that includes:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>One-time payment for perpetual access to the platform;</li>
            <li>Access to all features included in your purchase tier;</li>
            <li>Future updates and improvements as released;</li>
            <li>Technical support as specified in your purchase agreement.</li>
          </ul>
          <p>
            We reserve the right to modify the features and functionality of the Services, but your access to the core features included in your purchase will remain available.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">7. Test Creation and Usage</h2>
          <h3 className="text-xl font-medium">7.1 Simulated Tests</h3>
          <p>
            When creating and running tests through our platform, you agree to:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Clearly indicate that your test is for a product or service that is "coming soon" or in development;</li>
            <li>Not process any actual payments or financial transactions through test flows;</li>
            <li>Inform test participants that they are participating in a product validation test;</li>
            <li>Only collect information that is necessary for your test's purposes;</li>
            <li>Handle all collected data in accordance with applicable privacy laws;</li>
            <li>Not use test results to discriminate against any protected classes;</li>
            <li>Maintain transparency about the nature of your tests.</li>
          </ul>
          
          <h3 className="text-xl font-medium">7.2 Test Participant Rights</h3>
          <p>
            When conducting tests through our Services, you must respect test participant rights by:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Providing clear opt-out mechanisms;</li>
            <li>Honoring data deletion requests;</li>
            <li>Maintaining confidentiality of collected information;</li>
            <li>Using collected email addresses only for stated purposes;</li>
            <li>Providing access to your privacy policy and terms;</li>
            <li>Responding to participant inquiries about test nature and data usage.</li>
          </ul>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">8. Data Protection</h2>
          <p>
            Our platform collects and processes only the following types of data:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Email addresses (when voluntarily provided by test participants);</li>
            <li>Basic analytics data (page views, conversions, referrer information);</li>
            <li>Geographic location data at the country level;</li>
            <li>Device and browser information;</li>
            <li>Test interaction data.</li>
          </ul>
          <p>
            We do not process or store any actual payment information or sensitive personal data through our platform. All data processing is conducted in accordance with our Privacy Policy and applicable data protection laws.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">9. Intellectual Property</h2>
          <p>
            All intellectual property rights in the Services and their content are owned by us or our licensors. These rights are protected by Polish and international intellectual property laws.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">10. Limitation of Liability</h2>
          <p>
            To the extent permitted by applicable law:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages;</li>
            <li>Our total liability shall not exceed the amount paid by you for the Services;</li>
            <li>These limitations do not affect liability that cannot be excluded under Polish law.</li>
          </ul>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">11. Dispute Resolution</h2>
          <p>
            Any disputes arising from these Terms shall be resolved:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>For consumers within the EU: through the EU Online Dispute Resolution platform or competent courts in their country of residence;</li>
            <li>For all other users: through the courts in Poland;</li>
            <li>Alternative dispute resolution methods may be available under Polish law.</li>
          </ul>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">12. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms. We will notify you of any material changes. Your continued use of the Services after such modifications constitutes acceptance of the updated Terms.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">13. Contact Information</h2>
          <p>
            For any questions about these Terms, please contact us at:
          </p>
          <div className="p-4 bg-muted rounded-md">
            <p>Email: <a href="mailto:hello@willtheyconvert.com" className="text-primary hover:underline">hello@willtheyconvert.com</a></p>
          </div>
        </section>
        
        <Separator className="my-6" />
        
        <div className="text-center">
          <p className="text-muted-foreground">
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> | <Link to="/" className="text-primary hover:underline">Home</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms; 